"use client"

import { useEffect, useRef, useState, useCallback, useMemo } from "react"
import { getSupabaseBrowserClient } from "@/utils/supabase/client"
import {
  ChatMessagesFetchError,
  fetchChatMessagesWithRetry,
} from "@/lib/chat/fetch-chat-messages-with-retry"
import {
  normalizeListing,
  normalizeOrder,
  type ChatMessage,
} from "@/lib/chat/map-api-message"
import { canonicalChatRoomName } from "@/lib/chat/room-name"

export type { ChatMessage } from "@/lib/chat/map-api-message"

export type ChatMessagesLoadError = {
  message: string
  status: number | null
}

export function useRealtimeChat({ roomName, username }: { roomName: string; username: string }) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const [oldestMessageId, setOldestMessageId] = useState<string | null>(null)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<ChatMessagesLoadError | null>(null)
  const channelRef = useRef<any>(null)
  const dbChannelRef = useRef<any>(null)
  const supabase = useMemo(() => getSupabaseBrowserClient(), [])
  const CHUNK_SIZE = 50
  const canonicalRoomName = useMemo(() => canonicalChatRoomName(roomName), [roomName])

  const loadInitialMessages = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    setMessages([])
    setOldestMessageId(null)
    setHasMoreMessages(true)
    setConversationId(null)

    try {
      const result = await fetchChatMessagesWithRetry({
        roomName: canonicalRoomName,
        limit: CHUNK_SIZE,
      })
      const msgs = result.messages
      setMessages(msgs)
      if (result.conversationId) setConversationId(result.conversationId)

      if (msgs.length > 0) {
        setOldestMessageId(msgs[0].id)
        setHasMoreMessages(msgs.length === CHUNK_SIZE)
      } else {
        setOldestMessageId(null)
        setHasMoreMessages(false)
      }
    } catch (error) {
      const status = error instanceof ChatMessagesFetchError ? error.status : null
      const message =
        error instanceof ChatMessagesFetchError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Failed to load messages"
      console.error("[CHAT CLIENT] initial messages fetch failed", {
        room: canonicalRoomName,
        status,
        message,
      })
      setLoadError({ message, status })
      setMessages([])
      setOldestMessageId(null)
      setHasMoreMessages(false)
    } finally {
      setIsLoading(false)
    }
  }, [canonicalRoomName, CHUNK_SIZE])

  const loadOlderMessages = useCallback(async () => {
    if (!oldestMessageId || isLoadingOlder || !hasMoreMessages) return

    setIsLoadingOlder(true)
    try {
      const result = await fetchChatMessagesWithRetry({
        roomName: canonicalRoomName,
        limit: CHUNK_SIZE,
        before: oldestMessageId,
      })
      const olderMsgs = result.messages

      if (olderMsgs.length > 0) {
        setMessages((prev) => [...olderMsgs, ...prev])
        setOldestMessageId(olderMsgs[0].id)
        setHasMoreMessages(olderMsgs.length === CHUNK_SIZE)
      } else {
        setHasMoreMessages(false)
      }
    } catch (error) {
      console.error("Failed to load older messages:", error)
    } finally {
      setIsLoadingOlder(false)
    }
  }, [canonicalRoomName, oldestMessageId, isLoadingOlder, hasMoreMessages, CHUNK_SIZE])

  useEffect(() => {
    void loadInitialMessages()
  }, [loadInitialMessages])

  useEffect(() => {
    const channel = supabase.channel(canonicalRoomName)

    channel.on("broadcast", { event: "message" }, (payload) => {
      const raw = payload.payload as ChatMessage
      if (!raw?.id) return

      const normalizedListing = normalizeListing({
        listing: raw.listing,
        listing_id: (raw as any)?.listing_id ?? raw.listingId,
      })
      const normalizedOrder = normalizeOrder(raw.order)
      const message: ChatMessage = {
        ...raw,
        listing: normalizedListing,
        order: normalizedOrder,
        createdAt: raw.createdAt || new Date().toISOString(),
        listingId: raw.listingId ?? (raw as any)?.listing_id ?? normalizedListing?.id ?? null,
      }

      setMessages((prev) => {
        const exists = prev.find((m) => m.id === message.id || m.clientKey === message.clientKey)
        if (exists) return prev

        if (message.order?.orderId) {
          const dup = prev.find(
            (m) =>
              m.order?.orderId === message.order?.orderId &&
              (m.senderId || m.user?.name) === (message.senderId || message.user?.name) &&
              m.content.trim().toLowerCase() === (message.content || "").trim().toLowerCase() &&
              Math.abs(new Date(m.createdAt).getTime() - new Date(message.createdAt).getTime()) <= 5000,
          )
          if (dup) return prev
        }

        return [...prev, message]
      })
    })

    channel.subscribe((status) => {
      setIsConnected(status === "SUBSCRIBED")
    })

    channelRef.current = channel

    return () => {
      channel.unsubscribe()
      channelRef.current = null
    }
  }, [canonicalRoomName, supabase])

  useEffect(() => {
    if (!conversationId) return
    const dbChannel = supabase.channel(`messages:${conversationId}`)
    dbChannel.on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${conversationId}` },
      async () => {
        try {
          const result = await fetchChatMessagesWithRetry({
            roomName: canonicalRoomName,
            limit: 1,
          })
          const fresh = result.messages
          if (fresh.length === 0) return
          setMessages((prev) => {
            const exists = prev.find((m) => m.id === fresh[0].id || m.clientKey === fresh[0].clientKey)
            return exists ? prev : [...prev, ...fresh].sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""))
          })
        } catch {}
      },
    )
    dbChannel.on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "messages", filter: `conversation_id=eq.${conversationId}` },
      async (payload: any) => {
        try {
          const row = payload?.new as any
          const id = row?.id as string | undefined
          if (!id) return
          const nextOrder =
            normalizeOrder(row?.order) ??
            (row?.order_id || row?.seller_order_id
              ? normalizeOrder({
                  id: row?.order_id ?? undefined,
                  orderId: row?.order_id ?? undefined,
                  orderNumber: undefined,
                  sellerOrderId: row?.seller_order_id ?? undefined,
                  status: "pending",
                  total: 0,
                  itemCount: null,
                })
              : undefined)
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== id) return m
              return {
                ...m,
                attachmentId: row?.attachment_id ?? m.attachmentId ?? null,
                listingId: row?.listing_id ?? m.listingId ?? null,
                order: nextOrder ?? m.order,
              }
            }),
          )
        } catch {}
      },
    )
    dbChannel.subscribe()
    dbChannelRef.current = dbChannel
    return () => {
      dbChannel.unsubscribe()
      dbChannelRef.current = null
    }
  }, [conversationId, canonicalRoomName, supabase])

  function sendMessage(
    content: string,
    opts?: {
      id?: string
      userOverride?: { name: string; avatar_url?: string | null }
      imageUrl?: string | null
      pending?: boolean
      listing?: any
      order?: any
      localOnly?: boolean
      broadcastOnly?: boolean
      createdAt?: string
      attachmentId?: string | null
      replyToMessageId?: string | null
      replyTo?: ChatMessage["replyTo"]
    },
  ) {
    if (!content.trim() && !opts?.imageUrl) return

    const tempId = opts?.id || `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const createdAtValue = opts?.createdAt ?? new Date().toISOString()
    const message: ChatMessage = {
      id: tempId,
      content: content.trim() || "[Image]",
      user: opts?.userOverride || { name: username },
      createdAt: createdAtValue,
      senderId: username,
      clientKey: tempId,
      imageUrl: opts?.imageUrl || null,
      pending: opts?.pending,
      listing: opts?.listing,
      order: opts?.order,
      attachmentId: opts?.attachmentId ?? null,
      listingId: opts?.listing?.id ?? null,
      replyToMessageId: opts?.replyToMessageId ?? null,
      replyTo: opts?.replyTo ?? null,
    }

    const canBroadcast = Boolean(channelRef.current)

    if (!opts?.broadcastOnly) {
      setMessages((prev) => [...prev, message])
    }

    if (!opts?.localOnly && canBroadcast) {
      channelRef.current.send({
        type: "broadcast",
        event: "message",
        payload: message,
      })
    }
  }

  function replaceMessage(tempId: string, newMessage: Partial<ChatMessage> & { id: string }) {
    setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, ...newMessage, clientKey: newMessage.id } : m)))
  }

  function removeMessage(tempId: string) {
    setMessages((prev) => prev.filter((m) => m.id !== tempId))
  }

  const sortedMessages = useMemo(
    () =>
      [...messages].sort((a, b) => {
        const aTime = a.createdAt || ""
        const bTime = b.createdAt || ""
        return aTime.localeCompare(bTime)
      }),
    [messages],
  )

  return {
    messages: sortedMessages,
    sendMessage,
    replaceMessage,
    removeMessage,
    isConnected,
    isLoading,
    loadError,
    retryLoad: loadInitialMessages,
    loadOlderMessages,
    isLoadingOlder,
    hasMoreMessages,
  }
}
